import React, { useState, useEffect, useMemo, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { ANIMATION_DURATION, BREAKPOINTS, COLORS, styles, RANKS_DATA } from "./RankProgressionData";
import {
  Check,
  AlertTriangle,
  Info,
  TrendingUp,
  RefreshCw,
  PartyPopper,
  ChevronDown,
  ChevronUp,
  Star,
} from "lucide-react";

// Add CSS animation for skeleton
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .fade-in {
    animation: fadeIn ${ANIMATION_DURATION.slow}ms ease-out;
  }
  
  .scale-in {
    animation: scaleIn ${ANIMATION_DURATION.normal}ms cubic-bezier(.4,0,.2,1);
  }
`;
document.head.appendChild(styleSheet);

// ==================== MOCK DATA GENERATOR ====================
const generateMockData = (currentRankIndex = 2) => {
  const currentRank = RANKS_DATA[currentRankIndex];
  const nextRank = RANKS_DATA[currentRankIndex + 1] || RANKS_DATA[currentRankIndex];

  // Generate timeline based on current rank
  const timeline = [];
  const startDate = new Date("2024-01-01");

  for (let i = 0; i <= currentRankIndex; i++) {
    const daysToAdd = i * 45 + Math.floor(Math.random() * 30);
    const achievementDate = new Date(startDate);
    achievementDate.setDate(achievementDate.getDate() + daysToAdd);

    timeline.push({
      rank: RANKS_DATA[i].name,
      date: achievementDate.toISOString().split("T")[0],
    });
  }

  // Generate progress data based on rank type
  const progress = {};

  // Star ranks progress
  if (currentRank.name.includes("Star")) {
    progress.activeStatus = Math.random() > 0.1;
    progress.retailPV = Math.floor(Math.random() * 100) + 400;

    if (currentRank.name === "Star 250" || currentRank.name === "Star 500" || currentRank.name === "Star 1000") {
      progress.personallySponseredStarsOrg1 = Math.floor(Math.random() * 3) + 1;
      progress.personallySponseredStarsOrg2 = Math.floor(Math.random() * 3) + 1;
    }

    if (currentRank.name === "Star 500") {
      progress.paidAsStars = Math.floor(Math.random() * 3) + 3;
      progress.org1ActiveStars = Math.floor(Math.random() * 2) + 1;
      progress.org2ActiveStars = Math.floor(Math.random() * 2);
      progress.uniqueCustomers = Math.floor(Math.random() * 4) + 5;
    }

    if (currentRank.name === "Star 1000") {
      progress.totalStars = Math.floor(Math.random() * 10) + 20;
      progress.star500InFirstOrg = Math.random() > 0.3;
      progress.star500InSecondOrg = Math.random() > 0.4;
    }
  }

  // Executive ranks progress
  if (
    currentRank.name.includes("Executive") ||
    currentRank.name.includes("Diamond") ||
    currentRank.name.includes("Ambassador")
  ) {
    const rankLevel = currentRankIndex - 4; // Adjust for executive ranks starting at index 4

    // Activity level
    if (rankLevel <= 1) progress.activityPV = Math.floor(Math.random() * 30) + 40;
    else if (rankLevel <= 3) progress.activityPV = Math.floor(Math.random() * 50) + 80;
    else progress.activityPV = Math.floor(Math.random() * 100) + 150;

    // Group volumes
    const baseGV = [1000, 2500, 5000, 7500, 10000, 15000, 20000, 25000, 50000, 100000];
    const targetGV = baseGV[Math.min(rankLevel, baseGV.length - 1)] || 100000;

    progress.groupVolume = {
      org1: Math.floor(targetGV * (0.7 + Math.random() * 0.5)),
      org2: Math.floor(targetGV * (0.6 + Math.random() * 0.5)),
    };

    // Personal enrollment trees (for some ranks)
    if (rankLevel >= 0 && rankLevel <= 3) {
      progress.personalEnrollmentTrees = {
        tree1: Math.floor(Math.random() * 300) + 400,
        tree2: Math.floor(Math.random() * 300) + 350,
      };
    }

    // Aggregate revenue
    const baseRevenue = [2500, 7500, 20000, 50000, 100000, 150000, 200000, 250000];
    const targetRevenue = baseRevenue[Math.min(rankLevel, baseRevenue.length - 1)] || 250000;
    progress.aggregateRevenue = Math.floor(targetRevenue * (0.5 + Math.random() * 0.6));

    // Weeks qualified
    progress.weeksQualified = Math.floor(Math.random() * 2) + 1;

    // Berth organizations for highest ranks
    if (currentRank.name.includes("Black Diamond Ambassador")) {
      progress.berthOrganizations = [];
      const berthCount = Math.floor(Math.random() * 3) + 1;
      for (let i = 0; i < berthCount; i++) {
        progress.berthOrganizations.push({
          id: i + 1,
          volume: Math.floor(Math.random() * 80000) + 20000,
        });
      }
    }
  }

  // Add averaging status for executive ranks
  if (currentRank.name.includes("Executive") || currentRank.name.includes("Diamond")) {
    progress.averagingStatus = {
      week1: Math.random() > 0.3,
      week2: Math.random() > 0.2,
      week3: Math.random() > 0.4,
      week4: Math.random() > 0.5,
    };
  }

  return {
    currentRank: currentRank.name,
    nextRank: nextRank.name,
    progress,
    timeline,
  };
};

// ==================== UTILITY FUNCTIONS ====================
function formatNumber(num) {
  return new Intl.NumberFormat("en-IN").format(num);
}

function getPercentage(current, target) {
  return Math.min(100, Math.round((current / target) * 100));
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (e) => setMatches(e.matches);
    mediaQuery.addEventListener("change", handler);

    return () => mediaQuery.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

const ProgressBar = React.memo(({ label, current, target, color = "primary", showPercentage = true }) => {
  const progress = getPercentage(current, target);
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`);

  return (
    <div style={styles.progressContainer}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 16,
        }}
      >
        <span style={{ minWidth: 140, fontWeight: 600, fontSize: "0.875rem" }}>{label}</span>
        <div style={{ flex: 1 }}>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                ...(progress >= 100 ? styles.progressFillSuccess : {}),
                width: `${progress}%`,
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 100 }}>
          <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
            {formatNumber(current)}/{formatNumber(target)}
          </span>
          {showPercentage && <span style={{ fontSize: "0.75rem", color: COLORS.text.secondary }}>({progress}%)</span>}
        </div>
      </div>
    </div>
  );
});

const Chip = React.memo(({ label, color = "default", icon, onClick }) => {
  const [hover, setHover] = useState(false);

  const chipStyles = {
    success: styles.chipSuccess,
    default: styles.chipDefault,
    warning: styles.chipWarning,
    primary: { ...styles.chipDefault, background: COLORS.primary, color: "#fff" },
  };

  return (
    <span
      style={{
        ...styles.chip,
        ...chipStyles[color],
        ...(hover ? styles.chipHover : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {icon}
      {label}
    </span>
  );
});

const Alert = ({ severity = "info", children, icon }) => {
  const severityStyles = {
    success: styles.alertSuccess,
    info: styles.alertInfo,
    warning: styles.alertWarning,
  };

  const defaultIcons = {
    success: <Check size={20} color={COLORS.successDark} />,
    info: <Info size={20} color={COLORS.primaryDark} />,
    warning: <AlertTriangle size={20} color="#F57C00" />,
  };

  return (
    <div style={{ ...styles.alert, ...severityStyles[severity] }}>
      {icon || defaultIcons[severity]}
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

const Button = ({ children, onClick, disabled, variant = "primary" }) => {
  const [hover, setHover] = useState(false);

  return (
    <button
      style={{
        ...styles.button,
        ...(hover && !disabled ? styles.buttonHover : {}),
        ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const Skeleton = ({ height, width = "100%", borderRadius = 8 }) => (
  <div
    style={{
      ...styles.skeleton,
      height,
      width,
      borderRadius,
    }}
  />
);

// Rank selector for testing
const RankSelector = ({ currentRank, onChange }) => {
  return (
    <div
      style={{
        marginBottom: 24,
        padding: 16,
        background: "rgba(33,150,243,0.1)",
        borderRadius: 12,
        border: "1px solid rgba(33,150,243,0.3)",
      }}
    >
      <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
        Test Different Ranks (Development Only)
      </label>
      <select
        value={currentRank}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "8px 16px",
          borderRadius: 8,
          border: "1px solid #ddd",
          fontSize: "0.875rem",
          width: "100%",
          maxWidth: 300,
          cursor: "pointer",
        }}
      >
        {RANKS_DATA.map((rank, idx) => (
          <option key={rank.name} value={idx}>
            {rank.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const RankProgressionTimeline = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedRankIndex, setSelectedRankIndex] = useState(2); // Default to Star 500
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`);

  // Generate mock data based on selected rank
  const [distributorData, setDistributorData] = useState(() => generateMockData(selectedRankIndex));

  const activeStep = useMemo(() => {
    return RANKS_DATA.findIndex((r) => r.name === distributorData.currentRank);
  }, [distributorData.currentRank]);

  useEffect(() => {
    setExpandedStep(activeStep);
  }, [activeStep]);

  // Update data when rank selection changes
  useEffect(() => {
    setDistributorData(generateMockData(selectedRankIndex));
  }, [selectedRankIndex]);

  const fetchDistributorData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Regenerate mock data to simulate refresh
      setDistributorData(generateMockData(selectedRankIndex));
    } catch (err) {
      setError("Failed to load distributor data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedRankIndex]);

  useEffect(() => {
    fetchDistributorData();
  }, [fetchDistributorData]);

  const getMaintenanceRequirements = useCallback(() => {
    const rank = RANKS_DATA.find((r) => r.name === distributorData.currentRank);
    if (!rank) return [];

    const requirements = [];

    // Common active status
    if (distributorData.progress.activeStatus !== undefined) {
      requirements.push({
        label: "Active Status",
        value: distributorData.progress.activeStatus ? "Active" : "Inactive",
        complete: distributorData.progress.activeStatus,
      });
    }

    // Star rank specific requirements
    if (rank.name === "Star Distributor") {
      requirements.push({
        label: "Retail PV",
        value: `${distributorData.progress.retailPV || 0}/300`,
        complete: (distributorData.progress.retailPV || 0) >= 300,
      });
    }

    if (rank.name === "Star (250) Distributor") {
      requirements.push({
        label: "Stars in Org 1",
        value: `${distributorData.progress.personallySponseredStarsOrg1 || 0}/1`,
        complete: (distributorData.progress.personallySponseredStarsOrg1 || 0) >= 1,
      });
      requirements.push({
        label: "Stars in Org 2",
        value: `${distributorData.progress.personallySponseredStarsOrg2 || 0}/1`,
        complete: (distributorData.progress.personallySponseredStarsOrg2 || 0) >= 1,
      });
    }

    if (rank.name === "Star (500) Distributor") {
      requirements.push({
        label: "Personally Sponsored Stars",
        value: `${distributorData.progress.paidAsStars || 0}/5`,
        complete: (distributorData.progress.paidAsStars || 0) >= 5,
      });
      requirements.push({
        label: "Retail PV",
        value: `${distributorData.progress.retailPV || 0}/600`,
        complete: (distributorData.progress.retailPV || 0) >= 600,
      });
      requirements.push({
        label: "Stars in Organization 1",
        value: `${distributorData.progress.org1ActiveStars || 0}/1`,
        complete: (distributorData.progress.org1ActiveStars || 0) >= 1,
      });
      requirements.push({
        label: "Stars in Organization 2",
        value: `${distributorData.progress.org2ActiveStars || 0}/1`,
        complete: (distributorData.progress.org2ActiveStars || 0) >= 1,
      });
    }

    if (rank.name === "Star (1000) Distributor") {
      requirements.push({
        label: "Star 500 in Org 1",
        value: distributorData.progress.star500InFirstOrg ? "Yes" : "No",
        complete: distributorData.progress.star500InFirstOrg,
      });
      requirements.push({
        label: "Star 500 in Org 2",
        value: distributorData.progress.star500InSecondOrg ? "Yes" : "No",
        complete: distributorData.progress.star500InSecondOrg,
      });
      requirements.push({
        label: "750 GV in both orgs",
        value: "Weekly requirement",
        complete: true,
      });
    }

    // Executive rank requirements
    if (rank.name.includes("Executive") || rank.name.includes("Diamond") || rank.name.includes("Ambassador")) {
      const rankData = RANKS_DATA.find((r) => r.name === rank.name);
      const pvReq = parseInt(
        rankData?.requirements.find((req) => req.includes("Activity Level"))?.match(/\d+/)?.[0] || 50
      );
      const gvReq = parseInt(
        rankData?.requirements
          .find((req) => req.includes("Group Volume"))
          ?.match(/[\d,]+/)?.[0]
          ?.replace(",", "") || 1000
      );

      requirements.push({
        label: "Activity PV",
        value: `${distributorData.progress.activityPV || 0}/${pvReq}`,
        complete: (distributorData.progress.activityPV || 0) >= pvReq,
      });
      requirements.push({
        label: "Organization 1 GV",
        value: `${formatNumber(distributorData.progress.groupVolume?.org1 || 0)}/${formatNumber(gvReq)}`,
        complete: (distributorData.progress.groupVolume?.org1 || 0) >= gvReq,
      });
      requirements.push({
        label: "Organization 2 GV",
        value: `${formatNumber(distributorData.progress.groupVolume?.org2 || 0)}/${formatNumber(gvReq)}`,
        complete: (distributorData.progress.groupVolume?.org2 || 0) >= gvReq,
      });

      // Add averaging status
      const averagingComplete =
        Object.values(distributorData.progress.averagingStatus || {}).filter(Boolean).length >= 2;
      requirements.push({
        label: "Averaging Status",
        value: `${Object.values(distributorData.progress.averagingStatus || {}).filter(Boolean).length}/2 weeks`,
        complete: averagingComplete,
      });
    }

    // Black Diamond Ambassador specific
    if (rank.name.includes("Black Diamond Ambassador")) {
      const totalBerthVolume = (distributorData.progress.berthOrganizations || []).reduce(
        (sum, berth) => sum + berth.volume,
        0
      );

      requirements.push({
        label: "Berth Organizations",
        value: `${distributorData.progress.berthOrganizations?.length || 0} active`,
        complete: (distributorData.progress.berthOrganizations?.length || 0) >= 1,
      });
      requirements.push({
        label: "Total Berth Volume",
        value: formatNumber(totalBerthVolume),
        complete: totalBerthVolume > 0,
      });
    }

    return requirements;
  }, [distributorData]);

  const renderProgressSection = useCallback(() => {
    const currentRank = RANKS_DATA.find((r) => r.name === distributorData.currentRank);
    const nextRank = RANKS_DATA.find((r) => r.name === distributorData.nextRank);

    if (!nextRank || currentRank === nextRank) return null;

    // Star 1000 Progress
    if (distributorData.nextRank === "Star (1000) Distributor") {
      return (
        <div
          className="fade-in"
          style={{
            ...styles.glassCard,
            ...(hoveredCard === "star1000" ? styles.glassCardHover : {}),
            width: "100%",
            marginBottom: 20,
          }}
          onMouseEnter={() => setHoveredCard("star1000")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <h4 style={styles.sectionHeader}>Progress to Next Rank: {distributorData.nextRank}</h4>
          <ProgressBar label="Total Stars Required" current={distributorData.progress.totalStars || 0} target={25} />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginTop: 16 }}>
            <Chip
              label={`Star 500 in Org 1: ${distributorData.progress.star500InFirstOrg ? "✓" : "✗"}`}
              color={distributorData.progress.star500InFirstOrg ? "success" : "default"}
              icon={
                distributorData.progress.star500InFirstOrg ? (
                  <Check size={16} color="#fff" />
                ) : (
                  <AlertTriangle size={16} color={COLORS.warning} />
                )
              }
            />
            <Chip
              label={`Star 500 in Org 2: ${distributorData.progress.star500InSecondOrg ? "✓" : "✗"}`}
              color={distributorData.progress.star500InSecondOrg ? "success" : "default"}
              icon={
                distributorData.progress.star500InSecondOrg ? (
                  <Check size={16} color="#fff" />
                ) : (
                  <AlertTriangle size={16} color={COLORS.warning} />
                )
              }
            />
          </div>
          {(!distributorData.progress.star500InFirstOrg || !distributorData.progress.star500InSecondOrg) && (
            <Alert severity="warning">
              <span style={{ fontSize: "0.875rem" }}>
                You need at least one active Star 500 in each organization to qualify for Star 1000
              </span>
            </Alert>
          )}
        </div>
      );
    }

    // Executive/Diamond Progress
    if (
      nextRank.name.includes("Executive") ||
      nextRank.name.includes("Diamond") ||
      nextRank.name.includes("Ambassador")
    ) {
      const gvRequirement = parseInt(
        nextRank.requirements
          .find((req) => req.includes("Group Volume"))
          ?.match(/[\d,]+/)?.[0]
          ?.replace(",", "") || 1000
      );
      const aggregateRequirement = parseInt(
        nextRank.requirements
          .find((req) => req.includes("Aggregate Revenue"))
          ?.match(/[\d,]+/)?.[0]
          ?.replace(",", "") || 2500
      );
      const pvRequirement = parseInt(
        nextRank.requirements.find((req) => req.includes("Activity Level"))?.match(/\d+/)?.[0] || 50
      );

      return (
        <div
          className="fade-in"
          style={{
            ...styles.glassCard,
            ...(hoveredCard === "executive" ? styles.glassCardHover : {}),
            width: "100%",
            boxShadow: "0 4px 24px 0 rgba(33,150,243,0.10)",
            borderLeft: `6px solid ${COLORS.primary}`,
          }}
          onMouseEnter={() => setHoveredCard("executive")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <h4 style={styles.sectionHeader}>Progress to Next Rank: {distributorData.nextRank}</h4>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <h3 style={{ margin: 0, color: COLORS.primary, fontWeight: 700 }}>Executive Rank Progress</h3>
            <Chip
              icon={<TrendingUp size={16} />}
              label={`${getPercentage(
                (distributorData.progress.groupVolume?.org1 + distributorData.progress.groupVolume?.org2) / 2,
                gvRequirement
              )}% Complete`}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <ProgressBar
              label="Activity PV"
              current={distributorData.progress.activityPV || 0}
              target={pvRequirement}
              color="secondary"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24 }}>
            <ProgressBar
              label="Organization 1 GV"
              current={distributorData.progress.groupVolume?.org1 || 0}
              target={gvRequirement}
            />
            <ProgressBar
              label="Organization 2 GV"
              current={distributorData.progress.groupVolume?.org2 || 0}
              target={gvRequirement}
            />
            {aggregateRequirement && aggregateRequirement !== "Not specified" && (
              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <ProgressBar
                  label="Aggregate Revenue"
                  current={distributorData.progress.aggregateRevenue || 0}
                  target={aggregateRequirement}
                  color="secondary"
                />
              </div>
            )}
          </div>

          {/* Personal Enrollment Trees for early executive ranks */}
          {distributorData.progress.personalEnrollmentTrees && (
            <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24 }}>
              <ProgressBar
                label="Personal Tree 1"
                current={distributorData.progress.personalEnrollmentTrees.tree1 || 0}
                target={500}
              />
              <ProgressBar
                label="Personal Tree 2"
                current={distributorData.progress.personalEnrollmentTrees.tree2 || 0}
                target={500}
              />
            </div>
          )}

          {/* Averaging Status */}
          {distributorData.progress.averagingStatus && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: 600 }}>Averaging Status (50% volume required)</h4>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(distributorData.progress.averagingStatus).map(([week, qualified]) => (
                  <Chip
                    key={week}
                    label={`Week ${week.replace("week", "")}: ${qualified ? "✓" : "✗"}`}
                    color={qualified ? "success" : "default"}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Berth Organizations for highest ranks */}
          {distributorData.progress.berthOrganizations && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: 600 }}>Berth Organizations</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                {distributorData.progress.berthOrganizations.map((berth) => (
                  <div
                    key={berth.id}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: COLORS.grey[100],
                      border: `1px solid ${COLORS.grey[300]}`,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Berth {berth.id}</div>
                    <div style={{ fontSize: "0.875rem", color: COLORS.text.secondary }}>
                      Volume: {formatNumber(berth.volume)} PV
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(distributorData.progress.groupVolume?.org1 < gvRequirement ||
            distributorData.progress.groupVolume?.org2 < gvRequirement) && (
            <Alert severity="info">
              <span style={{ fontSize: "0.875rem" }}>
                Maintain balanced growth in both organizations to qualify for {distributorData.nextRank}
              </span>
            </Alert>
          )}
        </div>
      );
    }

    // Star 250 Progress
    if (distributorData.nextRank === "Star (250) Distributor") {
      return (
        <div
          className="fade-in"
          style={{
            ...styles.glassCard,
            ...(hoveredCard === "star250" ? styles.glassCardHover : {}),
            width: "100%",
            marginBottom: 20,
          }}
          onMouseEnter={() => setHoveredCard("star250")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <h4 style={styles.sectionHeader}>Progress to Next Rank: {distributorData.nextRank}</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <Chip
              label={`Active Star in Org 1: ${distributorData.progress.personallySponseredStarsOrg1 >= 1 ? "✓" : "✗"}`}
              color={distributorData.progress.personallySponseredStarsOrg1 >= 1 ? "success" : "default"}
              icon={
                distributorData.progress.personallySponseredStarsOrg1 >= 1 ? (
                  <Check size={16} color="#fff" />
                ) : (
                  <AlertTriangle size={16} color={COLORS.warning} />
                )
              }
            />
            <Chip
              label={`Active Star in Org 2: ${distributorData.progress.personallySponseredStarsOrg2 >= 1 ? "✓" : "✗"}`}
              color={distributorData.progress.personallySponseredStarsOrg2 >= 1 ? "success" : "default"}
              icon={
                distributorData.progress.personallySponseredStarsOrg2 >= 1 ? (
                  <Check size={16} color="#fff" />
                ) : (
                  <AlertTriangle size={16} color={COLORS.warning} />
                )
              }
            />
          </div>
        </div>
      );
    }

    // Star 500 Progress
    if (distributorData.nextRank === "Star (500) Distributor") {
      return (
        <div
          className="fade-in"
          style={{
            ...styles.glassCard,
            ...(hoveredCard === "star500" ? styles.glassCardHover : {}),
            width: "100%",
            marginBottom: 20,
          }}
          onMouseEnter={() => setHoveredCard("star500")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <h4 style={styles.sectionHeader}>Progress to Next Rank: {distributorData.nextRank}</h4>
          <ProgressBar label="Retail PV Required" current={distributorData.progress.retailPV || 0} target={600} />
          <ProgressBar
            label="Personally Sponsored Stars"
            current={distributorData.progress.paidAsStars || 0}
            target={5}
          />
          <ProgressBar label="Unique Customers" current={distributorData.progress.uniqueCustomers || 0} target={6} />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginTop: 16 }}>
            <Chip
              label={`Star in Org 1: ${distributorData.progress.org1ActiveStars >= 1 ? "✓" : "✗"}`}
              color={distributorData.progress.org1ActiveStars >= 1 ? "success" : "default"}
            />
            <Chip
              label={`Star in Org 2: ${distributorData.progress.org2ActiveStars >= 1 ? "✓" : "✗"}`}
              color={distributorData.progress.org2ActiveStars >= 1 ? "success" : "default"}
            />
          </div>
        </div>
      );
    }

    return null;
  }, [distributorData, hoveredCard, isMobile]);

  const renderTimelineStep = useCallback(
    (rank, idx) => {
      const achieved = distributorData.timeline.some((r) => r.rank === rank.name);
      const isCurrent = distributorData.currentRank === rank.name;
      const isFuture = idx > activeStep;
      const achievementDate = distributorData.timeline.find((r) => r.rank === rank.name)?.date;

      return (
        <div
          key={rank.name}
          className="scale-in"
          style={{
            ...styles.accordion,
            ...(isCurrent ? styles.accordionCurrent : {}),
            animationDelay: `${idx * 50}ms`,
          }}
        >
          <div
            style={{
              ...styles.accordionHeader,
              ...(expandedStep === idx ? { borderBottom: `1px solid ${COLORS.divider}` } : {}),
            }}
            onClick={() => setExpandedStep(expandedStep === idx ? null : idx)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
              <div
                style={{
                  ...styles.avatar,
                  ...(isCurrent ? styles.avatarCurrent : {}),
                  ...(achieved && !isCurrent ? styles.avatarAchieved : {}),
                  ...(!achieved && !isCurrent ? styles.avatarFuture : {}),
                }}
              >
                <img
                  src={rank.image_url}
                  alt={rank.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "50%",
                    background: "#fff",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    margin: 0,
                    fontWeight: isCurrent ? 800 : 600,
                    color: isCurrent ? COLORS.success : isFuture ? COLORS.text.secondary : COLORS.primaryDark,
                    fontSize: isCurrent ? "1.25rem" : "1.125rem",
                  }}
                >
                  {rank.name}
                </h4>
                {achievementDate && (
                  <span style={{ fontSize: "0.75rem", color: COLORS.text.secondary }}>
                    Achieved: {new Date(achievementDate).toLocaleDateString("en-IN")}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {isCurrent && <Chip label="Current" color="success" />}
                {achieved && !isCurrent && <Chip label="Achieved" color="primary" />}
                {isFuture && idx === activeStep + 1 && <Chip label="Next Goal" />}
              </div>
            </div>
            {expandedStep === idx ? <ChevronUp size={24} color={COLORS.text.secondary} /> : <ChevronDown size={24} color={COLORS.text.secondary} />}
          </div>

          {expandedStep === idx && (
            <div style={styles.accordionContent}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 24,
                }}
              >
                <div>
                  <h5 style={{ margin: "0 0 12px 0", fontWeight: 700, fontSize: "0.875rem" }}>Requirements</h5>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {rank.requirements.map((req, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <Check size={16} color={achieved ? COLORS.success : COLORS.grey[500]} />
                        <span style={{ fontSize: "0.875rem", flex: 1 }}>{req}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h5 style={{ margin: "0 0 12px 0", fontWeight: 700, fontSize: "0.875rem" }}>Rewards</h5>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {rank.rewards.map((reward, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <Star size={16} color={COLORS.primary} />
                        <span style={{ fontSize: "0.875rem", flex: 1 }}>{reward}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {rank.maintenance.length > 0 && (
                  <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                    <h5 style={{ margin: "0 0 12px 0", fontWeight: 700, fontSize: "0.875rem" }}>
                      Maintenance Requirements
                    </h5>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {rank.maintenance.map((req, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                          <Info size={16} color={COLORS.primaryLight} />
                          <span style={{ fontSize: "0.875rem", flex: 1 }}>{req}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {achieved && (
                <Alert severity="success" icon={<PartyPopper size={20} color={COLORS.successDark} />}>
                  <span style={{ fontWeight: 600 }}>Congratulations on achieving {rank.name}!</span>
                </Alert>
              )}
            </div>
          )}
        </div>
      );
    },
    [distributorData, activeStep, expandedStep, isMobile]
  );

  if (loading && !distributorData.currentRank) {
    return (
      <PageLayout activeSideMenu="1" pageTitle="Dashboard">
        <div style={styles.root}>
          <div style={{ width: "100%" }}>
            <Skeleton height={120} borderRadius={24} />
            <div style={{ height: 24 }} />
            <Skeleton height={200} borderRadius={24} />
            <div style={{ height: 24 }} />
            <Skeleton height={400} borderRadius={24} />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout activeSideMenu="1" pageTitle="Dashboard">
        <div style={styles.root}>
          <div
            style={{
              ...styles.glassCard,
              width: "100%",
              maxWidth: 600,
              textAlign: "center",
              boxShadow: "0 4px 24px 0 rgba(244,67,54,0.15)",
              borderLeft: `6px solid ${COLORS.error}`,
            }}
          >
            <AlertTriangle size={48} color={COLORS.error} />
            <h3 style={{ color: COLORS.error, margin: "16px 0 8px" }}>{error}</h3>
            <Button onClick={fetchDistributorData}>
              <RefreshCw size={20} color="#fff" />
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout activeSideMenu="1" pageTitle="Dashboard">
      <div style={styles.root}>
        {/* Development Rank Selector */}
        <RankSelector currentRank={selectedRankIndex} onChange={(value) => setSelectedRankIndex(parseInt(value))} />
        {/* Current Rank Maintenance Card */}
        <h2 className="mb-2 w-100">Rank Progression Journey</h2>

        {/* Header Section */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: 24,
            width: "100%",
            marginBottom: 32,
          }}
        >
          <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2.5rem",
                boxShadow: "0 4px 16px rgba(33,150,243,0.3)",
              }}
            >
              <img
                src={RANKS_DATA.find((r) => r.name === distributorData.currentRank)?.image_url}
                alt={distributorData.currentRank}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />
            </div>
            <div>
              <h4 style={{ margin: 0, color: COLORS.primary }}>{distributorData.currentRank}</h4>
              <p style={{ margin: 0, color: COLORS.text.secondary }}>Current Rank</p>
            </div>
          </div>

          <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 16, animationDelay: "200ms" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2rem",
                boxShadow: "0 4px 12px rgba(255,152,0,0.3)",
              }}
            >
              <img
                src={RANKS_DATA.find((r) => r.name === distributorData.nextRank)?.image_url}
                alt={distributorData.nextRank}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />
            </div>
            <div>
              <h4 style={{ margin: 0 }}>{distributorData.nextRank}</h4>
              <span style={{ color: COLORS.text.secondary }}>Next Rank</span>
            </div>
          </div>
        </div>
        <div
          className="scale-in"
          style={{
            ...styles.glassCard,
            ...(hoveredCard === "current" ? styles.glassCardHover : {}),
            width: "100%",
            boxShadow: "0 8px 32px 0 rgba(76,175,80,0.18)",
            borderLeft: `6px solid ${COLORS.success}`,
          }}
          onMouseEnter={() => setHoveredCard("current")}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div>
              <h4 style={{ margin: 0, color: COLORS.primary, fontWeight: 800 }}>Maintenance Requirements</h4>
              <p style={{ margin: "4px 0 0", color: COLORS.text.secondary, fontSize: "0.875rem" }}>
                To maintain your {distributorData.currentRank} status
              </p>
            </div>
            <Button onClick={fetchDistributorData} disabled={loading}>
              <RefreshCw size={20} color="#fff" />
              {loading ? "Loading..." : "Refresh"}
            </Button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`,
              gap: 12,
            }}
          >
            {getMaintenanceRequirements().map((req, index) => (
              <Chip
                key={index}
                label={`${req.label}: ${req.value}`}
                color={req.complete ? "success" : "warning"}
                icon={
                  req.complete ? (
                    <Check size={16} color="#fff" />
                  ) : (
                    <AlertTriangle size={16} color="#fff" />
                  )
                }
              />
            ))}
          </div>

          {/* Additional progress bars for certain ranks */}
          {(distributorData.currentRank === "Star (500) Distributor" ||
            distributorData.currentRank.includes("Executive") ||
            distributorData.currentRank.includes("Diamond") ||
            distributorData.currentRank.includes("Ambassador")) && (
            <>
              <div
                style={{
                  margin: "24px -24px",
                  padding: "16px 24px",
                  background: COLORS.grey[50],
                  borderTop: `1px solid ${COLORS.divider}`,
                  borderBottom: `1px solid ${COLORS.divider}`,
                }}
              >
                <p style={{ margin: 0, textAlign: "center", fontSize: "0.75rem", color: COLORS.text.secondary }}>
                  WEEKLY PROGRESS
                </p>
              </div>

              {distributorData.currentRank === "Star (500) Distributor" && (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24 }}>
                  <ProgressBar
                    label="Personally Sponsored Stars"
                    current={distributorData.progress.paidAsStars || 0}
                    target={5}
                  />
                  <ProgressBar label="Retail PV" current={distributorData.progress.retailPV || 0} target={600} />
                </div>
              )}

              {(distributorData.currentRank.includes("Executive") ||
                distributorData.currentRank.includes("Diamond") ||
                distributorData.currentRank.includes("Ambassador")) && (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24 }}>
                  <ProgressBar
                    label="Organization 1 GV"
                    current={distributorData.progress.groupVolume?.org1 || 0}
                    target={parseInt(
                      RANKS_DATA.find((r) => r.name === distributorData.currentRank)
                        ?.requirements.find((req) => req.includes("Group Volume"))
                        ?.match(/[\d,]+/)?.[0]
                        ?.replace(",", "") || 1000
                    )}
                  />
                  <ProgressBar
                    label="Organization 2 GV"
                    current={distributorData.progress.groupVolume?.org2 || 0}
                    target={parseInt(
                      RANKS_DATA.find((r) => r.name === distributorData.currentRank)
                        ?.requirements.find((req) => req.includes("Group Volume"))
                        ?.match(/[\d,]+/)?.[0]
                        ?.replace(",", "") || 1000
                    )}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Next Rank Progress Section */}
        {renderProgressSection()}

        {/* Timeline Section */}
        <div
          style={{
            width: "100%",
            background: "rgba(255,255,255,0.85)",
            borderRadius: 24,
            boxShadow: "0 2px 16px 0 rgba(33,150,243,0.10)",
            padding: isMobile ? 16 : 24,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              ...styles.sectionHeader,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h4>Timeline</h4>
                          <Button onClick={() => setExpandedStep(expandedStep === null ? activeStep : null)}>
               {expandedStep === null ? <ChevronDown size={20} color="#fff" /> : <ChevronUp size={20} color="#fff" />}
               {expandedStep === null ? "Expand Current" : "Collapse All"}
             </Button>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {RANKS_DATA.map((rank, idx) => renderTimelineStep(rank, idx))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default RankProgressionTimeline;
